"use client";

import React from 'react';

interface HeroBannerProps {
  /** 'full' shows the complete hero with left/right stat panels (overview dashboard).
   *  'compact' shows a slim branding strip for other screens. */
  variant?: 'full' | 'compact';
}

export default function HeroBanner({ variant = 'full' }: HeroBannerProps) {
  if (variant === 'compact') {
    return (
      <div className="relative w-full rounded-xl overflow-hidden border border-cyber-border bg-[#080B12] mb-5">
        {/* Animated grid */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              'linear-gradient(rgba(0,200,255,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(0,200,255,0.15) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
        {/* Glow orbs */}
        <div
          className="absolute left-0 top-1/2 -translate-y-1/2 w-48 h-48 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, #00c8ff 0%, transparent 70%)', filter: 'blur(30px)' }}
        />
        <div
          className="absolute right-0 top-1/2 -translate-y-1/2 w-48 h-48 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, #7c3aed 0%, transparent 70%)', filter: 'blur(30px)' }}
        />

        <div className="relative z-10 flex items-center justify-between px-5 py-3 gap-4">
          {/* Left — live indicator */}
          <div className="flex items-center gap-3">
            <span className="flex h-2 w-2 relative">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400" />
            </span>
            <span className="text-[10px] font-mono text-green-400 tracking-widest uppercase hidden sm:block">
              AI Engine Active
            </span>
          </div>

          {/* Center — logo + brand */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="relative">
              <div
                className="absolute inset-0 rounded-full opacity-25 animate-pulse"
                style={{ background: 'radial-gradient(circle, #00c8ff 0%, transparent 70%)', transform: 'scale(1.5)' }}
              />
              <div className="relative rounded-xl p-1.5 border border-cyber-blue/20 bg-black/40 backdrop-blur-sm">
                <img
                  src="/assets/images/Otek1_-SUBER_BEST_-1773730349362.jpeg"
                  alt="Otek1 Syber — 100% AI Cybersecurity Platform"
                  className="h-8 w-auto object-contain"
                  style={{ filter: 'drop-shadow(0 0 10px rgba(0,200,255,0.5))' }}
                />
              </div>
            </div>
            <div>
              <div className="text-[10px] font-mono text-cyber-blue tracking-[0.15em] uppercase leading-none">
                100% AI Powered
              </div>
              <div className="text-[9px] text-cyber-muted tracking-widest uppercase mt-0.5">
                Cybersecurity Platform
              </div>
            </div>
          </div>

          {/* Right — mini stats */}
          <div className="hidden sm:flex items-center gap-4">
            <div className="text-right">
              <div className="text-xs font-bold text-cyber-blue font-mono">99.97%</div>
              <div className="text-[9px] text-cyber-muted uppercase tracking-wide">Detection</div>
            </div>
            <div className="text-right">
              <div className="text-xs font-bold text-purple-400 font-mono">14.2K+</div>
              <div className="text-[9px] text-cyber-muted uppercase tracking-wide">Neutralized</div>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-mono text-yellow-400 tracking-widest uppercase hidden md:block">
                Threat Level: Elevated
              </span>
              <span className="flex h-2 w-2 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-yellow-400" />
              </span>
            </div>
          </div>
        </div>

        {/* Bottom accent line */}
        <div
          className="absolute bottom-0 left-0 right-0 h-px"
          style={{ background: 'linear-gradient(90deg, transparent, #00c8ff, #7c3aed, transparent)' }}
        />
      </div>
    );
  }

  // ── FULL variant (overview dashboard) ──
  return (
    <div
      className="relative w-full rounded-2xl overflow-hidden border border-cyber-border bg-[#080B12]"
      style={{ minHeight: '180px' }}
    >
      {/* Animated grid background */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage:
            'linear-gradient(rgba(0,200,255,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(0,200,255,0.15) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />
      {/* Glow orbs */}
      <div
        className="absolute left-0 top-1/2 -translate-y-1/2 w-64 h-64 rounded-full opacity-10"
        style={{ background: 'radial-gradient(circle, #00c8ff 0%, transparent 70%)', filter: 'blur(40px)' }}
      />
      <div
        className="absolute right-0 top-1/2 -translate-y-1/2 w-64 h-64 rounded-full opacity-10"
        style={{ background: 'radial-gradient(circle, #7c3aed 0%, transparent 70%)', filter: 'blur(40px)' }}
      />

      <div className="relative z-10 flex items-center justify-between px-6 py-6 gap-4">
        {/* ── LEFT PANEL ── */}
        <div className="flex flex-col gap-3 min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="flex h-2 w-2 relative">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400" />
            </span>
            <span className="text-[10px] font-mono text-green-400 tracking-widest uppercase">AI Engine Active</span>
          </div>

          <div>
            <div className="text-3xl font-bold text-white font-mono leading-none">
              99.97<span className="text-cyber-blue text-xl">%</span>
            </div>
            <div className="text-[10px] text-cyber-muted mt-0.5 uppercase tracking-wider">Threat Detection Accuracy</div>
          </div>

          <div className="flex gap-4">
            <div>
              <div className="text-sm font-bold text-cyber-blue font-mono">2.4M+</div>
              <div className="text-[9px] text-cyber-muted uppercase tracking-wide">Events/sec</div>
            </div>
            <div>
              <div className="text-sm font-bold text-purple-400 font-mono">847</div>
              <div className="text-[9px] text-cyber-muted uppercase tracking-wide">Models Active</div>
            </div>
            <div>
              <div className="text-sm font-bold text-green-400 font-mono">&lt;0.3ms</div>
              <div className="text-[9px] text-cyber-muted uppercase tracking-wide">Response Time</div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex gap-0.5 items-end h-6">
              {[4, 7, 5, 9, 6, 8, 5, 10, 7, 6, 9, 8].map((h, i) => (
                <div
                  key={i}
                  className="w-1 rounded-sm bg-cyber-blue opacity-70"
                  style={{ height: `${h * 2}px`, animationDelay: `${i * 0.1}s` }}
                />
              ))}
            </div>
            <span className="text-[9px] text-cyber-muted font-mono">LIVE NEURAL ACTIVITY</span>
          </div>
        </div>

        {/* ── CENTER LOGO ── */}
        <div className="flex flex-col items-center gap-3 flex-shrink-0 px-6">
          <div className="relative">
            <div
              className="absolute inset-0 rounded-full opacity-30 animate-pulse"
              style={{ background: 'radial-gradient(circle, #00c8ff 0%, transparent 70%)', transform: 'scale(1.4)' }}
            />
            <div className="relative rounded-2xl p-3 border border-cyber-blue/20 bg-black/40 backdrop-blur-sm">
              <img
                src="/assets/images/Otek1_-SUBER_BEST_-1773730349362.jpeg"
                alt="Otek1 Syber — 100% AI Cybersecurity Platform"
                className="h-40 w-auto object-contain"
                style={{ filter: 'drop-shadow(0 0 20px rgba(0,200,255,0.5))' }}
              />
            </div>
          </div>
          <div className="text-center">
            <div className="text-[10px] font-mono text-cyber-blue tracking-[0.2em] uppercase">100% AI Powered</div>
            <div className="text-[9px] text-cyber-muted tracking-widest uppercase mt-0.5">Cybersecurity Platform</div>
          </div>
        </div>

        {/* ── RIGHT PANEL ── */}
        <div className="flex flex-col gap-3 min-w-0 flex-1 items-end">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono text-yellow-400 tracking-widest uppercase">Threat Level: Elevated</span>
            <span className="flex h-2 w-2 relative">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-yellow-400" />
            </span>
          </div>

          <div className="text-right">
            <div className="text-3xl font-bold text-white font-mono leading-none">
              14.2K<span className="text-purple-400 text-xl">+</span>
            </div>
            <div className="text-[10px] text-cyber-muted mt-0.5 uppercase tracking-wider">Threats Neutralized Today</div>
          </div>

          <div className="flex gap-4 justify-end">
            <div className="text-right">
              <div className="text-sm font-bold text-yellow-400 font-mono">38</div>
              <div className="text-[9px] text-cyber-muted uppercase tracking-wide">Countries</div>
            </div>
            <div className="text-right">
              <div className="text-sm font-bold text-red-400 font-mono">3</div>
              <div className="text-[9px] text-cyber-muted uppercase tracking-wide">Critical Alerts</div>
            </div>
            <div className="text-right">
              <div className="text-sm font-bold text-cyber-blue font-mono">ISO 27001</div>
              <div className="text-[9px] text-cyber-muted uppercase tracking-wide">Certified</div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[9px] text-cyber-muted font-mono">THREAT SCAN ACTIVE</span>
            <div className="flex gap-0.5 items-end h-6">
              {[6, 9, 5, 8, 10, 4, 7, 6, 9, 5, 8, 7].map((h, i) => (
                <div
                  key={i}
                  className="w-1 rounded-sm bg-purple-400 opacity-70"
                  style={{ height: `${h * 2}px` }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom accent line */}
      <div
        className="absolute bottom-0 left-0 right-0 h-px"
        style={{ background: 'linear-gradient(90deg, transparent, #00c8ff, #7c3aed, transparent)' }}
      />
    </div>
  );
}
